import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { FaFilter } from 'react-icons/fa'
import { FaFilterCircleXmark } from 'react-icons/fa6'
import { useSearchParams } from 'react-router-dom'
import { z } from 'zod'

import { Breadcrumb } from '../../../components/Breadcumb'
import { Button } from '../../../components/Button'
import { Pagination } from '../../../components/Pagination'
import { getLogs, Log } from '../../../services/logsService'
import { FilterLogsFormModal } from './components/FilterLogsFormModal'
import { ModalSeeLogChanges } from './components/ModalSeeLogChanges'
import { LogsTable } from './components/Table/LogsTable'

export function Logs() {
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false)
  const [isSeeLogChangesModalOpen, setIsSeeLogChangesModalOpen] = useState(false)
  const [selectedLogChanges, setSelectedLogChangges] = useState<Log['changes'] | null>(null)
  const [selectedLogFile, setSelectedLogFile] = useState<Log['file'] | null>(null)

  const [searchParams, setSearchParams] = useSearchParams()

  const name = searchParams.get('name') || undefined
  const resource_id = searchParams.get('resource_id') || undefined
  const resource = searchParams.get('resource') || undefined
  const action = searchParams.get('action') || undefined

  const page = z.coerce
    .number()
    .parse(searchParams.get('page') ?? '1')

  function toggleFiltersModalOpen() {
    setIsFiltersModalOpen(open => !open)
  }

  function handleClearFilters() {
    setSearchParams(state => {
      state.delete('name')
      state.delete('resource_id')
      state.delete('resource')
      state.delete('action')
      state.set('page', '1')
      return state
    })
  }

  const { data: result } = useQuery({
    queryKey: ['logs', page, resource, resource_id, name, action],
    queryFn: () => getLogs({
      page,
      resource_id,
      resource,
      name,
      action,
    }),
  })

  function hasFilters() {
    if (resource_id || resource || name || action) {
      return true
    }
    return false
  }

  function handleSeeLogChanges(changes: object, file: string | null | undefined) {
    setSelectedLogChangges(changes)
    setSelectedLogFile(file)
    setIsSeeLogChangesModalOpen(true)
  }

  function handleCloseSeeLogChanges() {
    setSelectedLogChangges(null)
    setSelectedLogFile(null)
    setIsSeeLogChangesModalOpen(false)
  }

  function handlePaginate(page: number) {
    setSearchParams(state => {
      state.set('page', (page).toString())

      return state
    })
  }

  return (
    <div className="min-h-screen">
      <div className="flex flex-col mx-auto max-w-screen-lg">
        <div className="px-2 lg:px-0 my-4">
          <Breadcrumb
            items={[
              { label: 'Dashboard', path: '/dashboard' },
              { label: 'Logs', path: '/logs' },
            ]}
          />
        </div>
        <header className="flex flex-col sm:flex-row justify-between items-center m-4 lg:mx-0">
          <h1
            className="mb-4 sm:mb-0 font-bold text-2xl"
          >
            Logs
          </h1>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              onClick={handleClearFilters}
              size="icon"
              disabled={!hasFilters()}
              className="disabled:border-slate-400 disabled:text-slate-400 disabled:hover:border-slate-400 disabled:bg-transparent disabled:cursor-default"
              title="Limpar filtros"
            >
              <FaFilterCircleXmark />
            </Button>
            <Button
              variant="outline"
              onClick={toggleFiltersModalOpen}
              size="icon"
            >
              <FaFilter />
            </Button>
          </div>
        </header>
        <main className="px-2 lg:px-0 flex-1 h-full">
          {result &&
            <LogsTable
              logs={result.logs}
              onSeeChanges={handleSeeLogChanges}
            />}
        </main>
        <footer className="mx-4 lg:mx-0 mt-2">
          {result &&
            <Pagination page={page} totalCount={result.totalCount} pageQty={result.pageQty} onPageChange={handlePaginate} />}
        </footer>
      </div>
      {isFiltersModalOpen &&
        <FilterLogsFormModal onClose={toggleFiltersModalOpen} />}
      {isSeeLogChangesModalOpen &&
        <ModalSeeLogChanges onClose={handleCloseSeeLogChanges} changes={selectedLogChanges} file={selectedLogFile} />}
    </div>
  )
}
